import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import i18n from '../../i18n';

import { useCreateSubscriptionContext } from '../../context/createSubscriptionContext';

import Session from 'supertokens-web-js/recipe/session';
import { AppStoreContext } from '../../context/appStoreContext';
import { AppTourContext } from '../../context/appTourContext';
import SessionContext, { useSessionContext } from '../../context/sessionContext';
import { checkStorageStatus, fetchUserDetails } from '../../utils/services/authAPI';
import { ENTERPRISE_ENV } from '../../utils/helper';
import { getClientOrganisationDetails, licenseDetails } from '../../utils/services/onPremAPI';
import { message } from 'antd';
import { SERVICE_MESSAGES } from '../../utils/translationValues';
import { useEmbedding } from '../../context/embedContext';
import { isPopupWindow, notifyVendorPostSsoLogin } from '../../utils/embedHelper';
import { redirectToIdp } from '../../utils/authHelper';

export class SessionListeners {
  static eventListeners = new Set();
  static addEventListener = listener => {
    SessionListeners.eventListeners.add(listener);
    return () => {
      SessionListeners.eventListeners.delete(listener);
    };
  };
  static notifyListeners = async event => {
    // We copy this.eventListeners into a new array to "freeze" it for the loop
    // We do this to avoid an infinite loop in case one of the listeners causes a new listener to be added (e.g.: through re-rendering)
    console.log("Listeners :: ", SessionListeners.eventListeners);
    Array.from(SessionListeners.eventListeners).forEach(listener =>
      listener({
        ...event,
      }),
    );
  };
}

/**
 * This function handles calling APIs that should only be called once during mount (mostly on mount of a route/feature component).
 * It's split into multiple callbacks (fetch + handleResponse/handleError) because we expect fetch to take longer and
 * and the component may be unmounted during the first fetch, in which case we want to avoid updating state/redirecting.
 * This is especially relevant for development in strict mode with React 18 (and in the future for concurrent rendering).
 *
 * @param fetch This is a callback that is only called once on mount. Mostly it's for consuming tokens/doing one time only API calls
 * @param handleResponse This is called with the result of the first (fetch) call if it succeeds.
 * @param handleError This is called with the error of the first (fetch) call if it rejects.
 * @param startLoading Will start the whole process if this is set to true (or omitted). Mostly used to wait for session loading.
 */
const useOnMountAPICall = (
  fetch /*: () => Promise<T>*/,
  handleResponse /*: (consumeResp: T) => Promise<void>*/,
  handleError /*?: (err: unknown, consumeResp: T | undefined) => void*/,
  startLoading = true,
) => {
  const consumeReq = useRef();

  const [error, setError] = useState(undefined);
  useEffect(() => {
    const effect = async signal => {
      let resp;
      try {
        if (consumeReq.current === undefined) {
          consumeReq.current = fetch();
        }

        resp = await consumeReq.current;

        if (!signal.aborted) {
          void handleResponse(resp);
        }
      } catch (err) {
        if (!signal.aborted) {
          if (handleError !== undefined) {
            handleError(err, resp);
          } else {
            setError(err);
          }
        }
      }
    };
    if (startLoading) {
      const ctrl = new AbortController();

      void effect(ctrl.signal);
      return () => {
        ctrl.abort();
      };
    }
    return;
  }, [setError, consumeReq, fetch, handleResponse, handleError, startLoading]);

  if (error) {
    throw error;
  }
};

const INITIAL_SESSION_CONTEXT = { loading: true, organizationSetup: undefined };

const SessionAuth = ({ children, isEmbedded, ...props }) => {
  const { embedState } = useEmbedding();
  const requireAuth = useRef(props.requireAuth);
  if (props.requireAuth !== requireAuth.current) {
    throw new Error(
      'requireAuth prop should not change. If you are seeing this, it probably means that you are using SessionAuth in multiple routes with different values for requireAuth. To solve this, try adding the "key" prop to all uses of SessionAuth like <SessionAuth key="someUniqueKeyPerRoute" requireAuth={...}>',
    );
  }

  const {
    setLoginVisible,
    setEmailVerificationVisible,
    setChekOutVerification,
    setUserDetails,
    setIsEnterprise,
    onboardingStatus,
    setOnboardingStatus,
    setIsLicenseExpired,
    setIsLdapConfigured,
    checkUserAccessRoles,
    setSSOSetup,
    setLicenseDetails,
    setSessionExpired
  } = useContext(AppStoreContext);
  const { setFirstLogin } = useContext(AppTourContext);
  const session = useRef();
  let history = useHistory();

  const contextSelector = useCallback(store => ({ ...store }), []);
  const [context, setContext] = useSessionContext(contextSelector);

  const redirectToLogin = useCallback(() => {
    setLoginVisible(true);
    setOnboardingStatus(false);
  }, [setLoginVisible]);

  const redirectToEmailVerification = useCallback(() => {
    setEmailVerificationVisible(true);
  }, [setEmailVerificationVisible]);

  const buildContext = useCallback(async () => {
    console.log('buildContext1: ', context);

    if (session.current === undefined) {
      session.current = Session;
    }
    console.log('session: ', Session);
    const sessionExists = await session.current.doesSessionExist({
      userContext: {},
    });

    console.log({ sessionExists });

    if (sessionExists === false) {
      return {
        loading: false,
        doesSessionExist: false,
        accessTokenPayload: {},
        invalidClaims: [],
        userId: '',
      };
    }

    /*let invalidClaims;
        try {
            invalidClaims = await session.current.validateClaims({
                overrideGlobalClaimValidators: props.overrideGlobalClaimValidators,
                userContext,
            });
        } catch (err) {
            // These errors should only come from getAccessTokenPayloadSecurely inside validateClaims if refreshing a claim cleared the session
            // Which means that the session was most likely cleared, meaning returning false is right.
            // This might also happen if the user provides an override or a custom claim validator that throws (or if we have a bug)
            // In which case the session will not be cleared so we rethrow the error
            if (
                await session.current.doesSessionExist({
                    userContext,
                })
            ) {
                throw err;
            }
            return {
                loading: false,
                doesSessionExist: false,
                accessTokenPayload: {},
                invalidClaims: [],
                userId: "",
            };
        }
        // TODO: basing redirection on userContext could break in certain edge-cases involving async validators
        const invalidClaimRedirectToPath = popInvalidClaimRedirectPathFromContext(userContext);*/

    /*try {*/
    return {
      loading: false,
      doesSessionExist: true,
      /*invalidClaims,
                invalidClaimRedirectToPath,*/
      accessTokenPayload: await session.current.getAccessTokenPayloadSecurely({
        userContext: {},
      }),
      userId: await session.current.getUserId({
        userContext: {},
      }),
    };
    /*} catch (err) {
            if (
                await session.current.doesSessionExist({
                    userContext,
                })
            ) {
                throw err;
            }
            // This means that loading the access token or the userId failed
            // This may happen if the server cleared the error since the validation was done which should be extremely rare
            return {
                loading: false,
                doesSessionExist: false,
                accessTokenPayload: {},
                invalidClaims: [],
                userId: "",
            };
        }*/
  }, []);

  const setInitialContextAndMaybeRedirect = useCallback(
    async toSetContext => {
      // toSetContext.doesSessionExist = false;
      console.log('redirect: ', toSetContext, context, props.setSessionInfo);

      if (context.loading === false) {
        if (props.setSessionInfo === undefined) {
          return;
        }
      }     

      if (await Session.doesSessionExist()) {
        props.setSessionInfo && props.setSessionInfo({ isActive: true, isLoggedIn: false });
      }
      if (props.doRedirection !== false) {
        // Verifying if user email has been verified by supetokens
        let validationErrors;
        if (await Session.doesSessionExist() && !ENTERPRISE_ENV) {
          validationErrors = await Session.validateClaims();
          setLoginVisible(false);
        }

        if (!toSetContext.doesSessionExist && props.requireAuth !== false && !isEmbedded) {
          // props.setSessionExists(false);
          redirectToIdp(redirectToLogin);
          // return;
        } else if (toSetContext.invalidClaimRedirectToPath !== undefined) {
          /*await SuperTokens.getInstanceOrThrow().redirectToUrl(
                        toSetContext.invalidClaimRedirectToPath,
                        history
                    );*/
          // return;
        }
        if(ENTERPRISE_ENV && await Session.doesSessionExist()) {
          const response = await licenseDetails();

          const organisationDetails = await getClientOrganisationDetails();
          setSSOSetup(organisationDetails);
          setContext({ organisationDetails: organisationDetails });
          if(response) {
            setLicenseDetails(response);
            setIsLdapConfigured(response.isLDAPConfigured);
            if(response.licenseStatus === 20) {
              setIsLicenseExpired(true);
              return;
            }
          } else {
            message.error(i18n.t(`SERVICE_MESSAGES:${SERVICE_MESSAGES.GENERAL_OOPS_ERROR.key}`, SERVICE_MESSAGES.GENERAL_OOPS_ERROR.value));
            return;
          }
        }

        let doesCheckoutExit;
        //When email is verified
        if (validationErrors?.length === 0 || (toSetContext.doesSessionExist && ENTERPRISE_ENV)) {
          if (!toSetContext.doesSessionExist) {
            setEmailVerificationVisible(false);
            redirectToLogin();
            return;
          }

          if (props.setSessionInfo) return;

          // Check if checkout is completed. Routing should be done accordinglys
          doesCheckoutExit = await fetchUserDetails();

          if (doesCheckoutExit?.statusCode === 1903) {
            const userData = doesCheckoutExit?.data;
            if (userData) {
              userData?.languageCode && i18n.changeLanguage(userData?.languageCode);
            }
            const orgData = await getClientOrganisationDetails();
            userData.features = orgData.features ?? null;
            setUserDetails(userData);
            setIsEnterprise(userData.licenseType === "ENTERPRISE");
            toSetContext.userData = userData;
            if (toSetContext.doesSessionExist && userData?.orgStatus === 'active') {
              setOnboardingStatus(true);
              setChekOutVerification(false);
              if (window.location.pathname.toLowerCase() === '/signup'
                || window.location.pathname.toLowerCase() === '/ldap') {
                history.push('/')
              }
              if (window.location.pathname.toLowerCase() === '/redirect') {
                isPopupWindow ? notifyVendorPostSsoLogin(true) : history.push('/');
              }
            } else {
              setChekOutVerification(true);
            }
            // Enable app tour
            if(['DESIGNER', 'PUBLISHER'].some(role => userData.roles.includes(role)) && userData?.licenseType === "SAAS") {
              setFirstLogin(toSetContext?.accessTokenPayload?.firstLogin || context?.accessTokenPayload?.firstLogin || false);
            }
            if(userData?.licenseType !== "ENTERPRISE" && userData?.userType === 'client') {
              const response = await checkStorageStatus();
              if(response?.storageCreated) {
                toSetContext.organizationSetup = 'completed';
              }
            }
          }
        } else {
          if (toSetContext.doesSessionExist) {
            /* Redirecting to email verification page if session exists and email not verified */
            console.log('redirect to EV');
            redirectToEmailVerification();
          }
        }
      }

      delete toSetContext.invalidClaimRedirectToPath;

      setContext(toSetContext);
    },
    [props.doRedirection, props.requireAuth, redirectToLogin, context],
  );

  useOnMountAPICall(buildContext, setInitialContextAndMaybeRedirect);

  const onBoarding = async (accessTokenPayload) => {
    let doesCheckoutExit;
    let validationErrors;

    // check session after trigger login button or signup button
    if (await Session.doesSessionExist()) {
      setLoginVisible(false);
      // setEmailVerificationVisible(true);

      // email verification
      validationErrors = await Session.validateClaims();
    }

    if (validationErrors?.length === 0 || ENTERPRISE_ENV) {
      setEmailVerificationVisible(false);

      if (props.setSessionInfo) return;
      // checkout verification
      doesCheckoutExit = await fetchUserDetails();
      if (doesCheckoutExit) {
        let userLanguage = doesCheckoutExit?.data?.languageCode;
        userLanguage && i18n.changeLanguage(userLanguage);
      }

      console.log('doesCheckoutExit', doesCheckoutExit)

      if (doesCheckoutExit?.statusCode === 1903) {
        const userData = doesCheckoutExit.data;
        const orgData = await getClientOrganisationDetails();
        userData.features = orgData.features ?? null;
        setUserDetails(userData);
        setIsEnterprise(userData.licenseType === "ENTERPRISE");
        if(userData?.licenseType === "ENTERPRISE") {
          const licenseInfo = await licenseDetails();
          if(licenseInfo) {
            setLicenseDetails(licenseInfo);
            setIsLdapConfigured(licenseInfo?.isLDAPConfigured);
          }
          const organisationDetails = await getClientOrganisationDetails();
          setSSOSetup(organisationDetails);
        }
        setContext({
          userData
        });
        if (window.location.pathname.toLowerCase() === '/ldap') {
          history.push('/')
        }
        if (userData?.orgStatus === 'active') {
          setOnboardingStatus(true);
          setChekOutVerification(false);
        } else {
          setChekOutVerification(true);
        }
        // Enable app tour
        if(['DESIGNER', 'PUBLISHER'].some(role => userData.roles.includes(role)) && userData.licenseType === "SAAS") {
          setFirstLogin(accessTokenPayload?.firstLogin || false);
        }       
        if(userData?.licenseType !== "ENTERPRISE" && userData?.userType === 'client') {
          const response = await checkStorageStatus();
          if(response?.storageCreated) {
            setContext({ organizationSetup: 'completed' });
          }
        }
      }
    } else {
      setEmailVerificationVisible(true);
    }
  };

  // subscribe to events on mount
  useEffect(() => {
    async function onHandleEvent(event) {
      console.log({ event });
      const sessionExists = await session.current.doesSessionExist({
        userContext: {},
      });


      const getAccessTokenInfo = async () => {
        const accessTokenPayload = await session.current.getAccessTokenPayloadSecurely({
          userContext: {},
        });
        return accessTokenPayload;
      }

      let accessTokenPayload;

      switch (event.action) {
        // We intentionally fall through as they are all handled the same way.
        case 'SESSION_CREATED':
          console.log('Session Created!!! ', event);
          // When session does not exist and after clicking Proceed To Checkout button, login page is shown
          // After successful login, setting the state to handle routing to Stripe Checkout page
          if (props.setSessionInfo) {
            props.setSessionInfo({ isActive: true, isLoggedIn: true });
          }

          accessTokenPayload = await getAccessTokenInfo();
          setContext({
            ...event.sessionContext,
            loading: false,
            doesSessionExist: true,
            accessTokenPayload,
            userId: accessTokenPayload.sub,
            invalidClaims: [],
          });
          onBoarding(accessTokenPayload);
          return;
        case 'REFRESH_SESSION':
          console.log('Session Refresh!!! ', event);
          return;
        case 'ACCESS_TOKEN_PAYLOAD_UPDATED':
          accessTokenPayload = await getAccessTokenInfo();
          console.log('ACCESS_TOKEN_PAYLOAD_UPDATED!!! ', context, accessTokenPayload);
          setContext({
            ...event.sessionContext,
            accessTokenPayload,
            loading: false,
            invalidClaims: [],
          });
          onBoarding();
          return;
        case 'API_INVALID_CLAIM': {
          console.log('API_INVALID_CLAIM!!! ', event);
          return;
        }
        case 'SIGN_OUT': {
          console.log('SIGN_OUT :: ', event);
          setOnboardingStatus(false);
          setContext({
            ...event.sessionContext,
            doesSessionExist: false,
            loading: false,
            invalidClaims: [],
          });
          if (embedState?.isEmbedded) {
            setSessionExpired(true);
          } else {
            redirectToLogin();
          }
          return;
        }
        case 'UNAUTHORISED':
          console.log('UNAUTHORISED :: ', event);
          setContext({
            ...event.sessionContext,
            loading: false,
            invalidClaims: [],
          });
          const sessionExists = await Session.doesSessionExist();
          if (props.onSessionExpired !== undefined) {
            props.onSessionExpired();
          } else if (props.requireAuth !== false && props.doRedirection !== false) {
            if (!sessionExists && embedState?.isEmbedded) {
              setSessionExpired(true);
            } else {
              redirectToIdp(redirectToLogin);
            }
          }
          return;
        default:
          console.log('Default case:');
          break;
      }
    }

    if (session.current === undefined) {
      session.current = Session;
    }

    if (context.loading === false) {
      // we return here cause addEventListener returns a function that removes
      // the listener, and this function will be called by useEffect when
      // onHandleEvent changes or if the component is unmounting.
      console.log("On handle registered!!!");
      return SessionListeners.addEventListener(onHandleEvent);
    }
    console.log("On handle not registered!!!");
    return undefined;
  }, [props, setContext, context.loading]);

  if (!context.loading && isEmbedded) {
    return <>{children}</>
  }

  if (!onboardingStatus) {
    return null;
  }

  if (
    props.requireAuth !== false &&
    props.visible !== true &&
    (context.loading || !context.doesSessionExist)
  ) {
    return null;
  }


  return <>{children}</>;
};

const SessionAuthWrapper = props => {
  const store = useCreateSubscriptionContext(INITIAL_SESSION_CONTEXT, undefined, false);
  const { path } = props;

  return (
    <SessionContext.Provider value={store}>
      <SessionAuth key={path} {...props}> {props.children} </SessionAuth>
    </SessionContext.Provider>
  );
};

export default SessionAuthWrapper;
